import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Collapse, CollapseCallbackArgs } from 'react-collapse';
import { FormattedMessage } from 'react-intl';
import { useCheckoutState } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import getDeliveryBadgeInfo from '../../../utils/getDeliveryBadgeInfo';
import useIsMobileDevice from '../../../utils/useIsMobileDevice';
import DeliveryEstimate from '../../atoms/DeliveryEstimate';
import DeliverySummaryBadge from '../../atoms/DeliverySummaryBadge';
import MainHeadline from '../../atoms/MainHeadline';
import Price from '../../atoms/Price';
import BasketItem from '../../molecules/BasketItem';
import PriceSummary from '../../molecules/PriceSummary';
import './style.scss';

const Basket = ({ desktopStickyStyle }: { desktopStickyStyle?: React.CSSProperties }) => {
  const {
    checkoutState: {
      orderState: { basket, shipping, payment },
    },
  } = useCheckoutState();

  const basketTotal = basket.total.costWithTaxWithVouchersWithServiceCostsWithGiftCard;
  const basketSubtotal = basket.total.costWithTaxWithoutAbsoluteVouchersWithoutServiceCosts;
  const basketShipping = basket.total.costShippingServiceCosts;
  const giftCardTotal = payment?.giftCards?.[0]?.recurringPaymentInformation?.remainingAmount;
  const selectedCarrier = shipping?.selected?.[0].carrierGroupKey;

  const isMobile = useIsMobileDevice();
  const { items, packages, reduction } = basket.packages
    .filter(({ isAvailable }) => isAvailable)
    .reduce(getDeliveryBadgeInfo, { items: 0, packages: 0, reduction: 0 });
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [showDeliveryBadge, setShowDeliveryBadge] = useState(false);
  const isBreadcrumbsEnabled = config.features.breadcrumbs.enabled;

  useEffect(() => {
    if (!isMobile) {
      setIsExpanded(true);
    }
  }, [isMobile]);

  return (
    <div className="basket-container">
      <div className="basket__modal" style={isMobile ? { top: 'unset' } : desktopStickyStyle}>
        <div className={classNames('basket', { 'basket--web-component': IS_WEB_COMPONENT })} data-test-id="basket">
          <MainHeadline
            className={classNames('basket__headline', {
              ['basket__headline--breadcrumbs-offset']: isBreadcrumbsEnabled,
            })}
          >
            <FormattedMessage id="basket.headline" />
            <div className="basket__expand">
              <Price price={basketTotal} data-test-id="basket-total-price" />
              <img
                className="basket__chevron-icon"
                onClick={() => setIsExpanded(!isExpanded)}
                src={getAssetUrl('inputs/chevronDown.svg')}
                style={{ transform: `rotate(${isExpanded ? '180deg' : '0'})` }}
              />
            </div>
          </MainHeadline>
          <div className="basket__inner">
            {showDeliveryBadge && (
              <DeliverySummaryBadge
                deliveries={packages}
                quantity={items}
                reduction={reduction}
                carrier={selectedCarrier}
                currency={config.currency}
              />
            )}
            <Collapse
              isOpened={isExpanded}
              onWork={({ isOpened }: CollapseCallbackArgs) => isOpened && setShowDeliveryBadge(false)}
              onRest={({ isOpened }: CollapseCallbackArgs) => !isOpened && setShowDeliveryBadge(true)}
            >
              <div className="basket__middle">
                {basket.packages
                  .filter(({ isAvailable }) => isAvailable)
                  .map((pkg, index) => (
                    <div key={index}>
                      {!!pkg.items.length && (
                        <div className="basket__overview">
                          <DeliveryEstimate
                            min={pkg.deliveryDate.min}
                            max={pkg.deliveryDate.max}
                            carrier={selectedCarrier} // TODO: Use carrierGroupKey from pkg.applicableDeliverOptions
                          />
                        </div>
                      )}
                      <div className="basket__content">
                        {pkg.items.map(item => (
                          <BasketItem key={item.id} item={item} />
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
              <PriceSummary
                total={basketTotal}
                shipping={basketShipping}
                subtotal={basketSubtotal}
                giftCard={giftCardTotal}
              />
            </Collapse>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Basket;
